// recents.js — persistent "recent & frequent" store for the Command Palette.
// WL (Fable Hail Mary Wave 5). Pure + fully unit-tested (recents.test.js).
//
// WHY IT EXISTS
// An empty palette (no query typed) should not be a blank box — it should offer
// the things this user actually jumps to most. We persist a small MRU/MFU list
// in localStorage so "open palette → Enter" repeats the last jump, and frequent
// destinations float to the top over time.
//
// Each stored entry is a self-contained SNAPSHOT of the item the moment it was
// used {id, kind, label, sublabel, path, portrait, wheelName, wheelId} plus
// {ts, count}.
// Storing the snapshot (not just an id) means the empty state renders instantly
// with no refetch — even a wheel row shows its saved name + portrait offline.
//
// Ranking = frequency first (count), recency as the tie-break. Bounded to MAX
// entries so the store can never grow without limit.

export const RECENTS_KEY = "iyuga.cmdPalette.recents.v1"; // gitleaks:allow — localStorage key name, not a secret
export const MAX_RECENTS = 50;
export const DEFAULT_SHOWN = 7;

const safeParse = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Read the raw recents array (newest-first is NOT guaranteed — use rankRecents).
 * @param {Storage} [storage] injectable for tests (defaults to localStorage)
 */
export function readRecents(storage) {
  const store = storage || getStorage();
  if (!store) return [];
  return safeParse(store.getItem(RECENTS_KEY));
}

function getStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null; // Safari private mode / blocked storage
  }
}

/**
 * Rank stored entries: most-frequent first, most-recent as the tie-break.
 * @param {Array} entries
 * @returns {Array} a new sorted array
 */
export function rankRecents(entries) {
  return [...(entries || [])].sort((a, b) => {
    if ((b.count || 0) !== (a.count || 0)) return (b.count || 0) - (a.count || 0);
    return (b.ts || 0) - (a.ts || 0);
  });
}

/**
 * Return up to `limit` ranked recent items ready to render.
 */
export function getRecents(limit = DEFAULT_SHOWN, storage) {
  return rankRecents(readRecents(storage)).slice(0, limit);
}

/**
 * Record a use of an item: bump its count + timestamp, or insert it.
 * Returns the updated (unranked) array. Safe to call when storage is blocked.
 *
 * @param {{id:string, kind:string, label:string, sublabel?:string, path?:string,
 *          portrait?:string, wheelName?:string, wheelId?:(string|number)}} item
 * @param {number} [now] injectable clock for tests
 * @param {Storage} [storage]
 */
export function recordUse(item, now = Date.now(), storage) {
  const store = storage || getStorage();
  if (!item || !item.id) return readRecents(store);
  const entries = readRecents(store);
  const existing = entries.find((e) => e.id === item.id);

  const snapshot = {
    id: item.id,
    kind: item.kind,
    label: item.label,
    sublabel: item.sublabel,
    path: item.path,
    portrait: item.portrait,
    wheelName: item.wheelName,
    wheelId: item.wheelId,
  };

  let next;
  if (existing) {
    next = entries.map((e) =>
      e.id === item.id
        ? { ...e, ...snapshot, count: (e.count || 0) + 1, ts: now }
        : e
    );
  } else {
    next = [...entries, { ...snapshot, count: 1, ts: now }];
  }

  // Bound the store: keep the top MAX_RECENTS by rank, drop the rest.
  next = rankRecents(next).slice(0, MAX_RECENTS);

  if (store) {
    try {
      store.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch {
      /* storage full / blocked — recents are best-effort, never fatal */
    }
  }
  return next;
}

/**
 * Remove a single entry (e.g. a wheel that no longer exists), returning the
 * updated array.
 */
export function removeRecent(id, storage) {
  const store = storage || getStorage();
  const next = readRecents(store).filter((e) => e.id !== id);
  if (store) {
    try {
      store.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }
  return next;
}

/** Clear everything (used by tests / a future "clear recents" affordance). */
export function clearRecents(storage) {
  const store = storage || getStorage();
  if (store) {
    try {
      store.removeItem(RECENTS_KEY);
    } catch {
      /* ignore */
    }
  }
}
